import React from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, FileSearch, Loader2 } from 'lucide-react';

const CASE_ANALYSIS_MODES = new Set([
    'case_trial_analysis_queued',
    'case_trial_analysis_complete',
    'case_trial_analysis_failed',
]);

const positiveInteger = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
};

const getCaseIdFromUrl = (url) => {
    const match = String(url || '').match(/\/summaries\/(\d+)/i);
    return match ? positiveInteger(match[1]) : null;
};

const normalizePercent = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.min(100, Math.max(0, Math.round(parsed)));
};

const getDocumentCoverage = (runPlan) => {
    const documentStep = (Array.isArray(runPlan?.steps) ? runPlan.steps : [])
        .find(step => String(step?.id || '').toLowerCase() === 'documents');
    const progressText = (Array.isArray(documentStep?.meta) ? documentStep.meta : [])
        .map(String)
        .find(item => /\d+\s+of\s+\d+\s+documents/i.test(item));
    const match = String(progressText || '').match(/(\d+)\s+of\s+(\d+)\s+documents/i);
    return match ? { processed: Number(match[1]), total: Number(match[2]) } : null;
};

export const getCaseAnalysisChatCardData = (message, answerOverride = null) => {
    const answer = answerOverride || message?.answer || message?.metadata?.intelligence?.answer || null;
    const mode = String(answer?.mode || message?.metadata?.intelligence?.retrieval_mode || '').trim().toLowerCase();
    const taskType = String(
        answer?.proactive_ai?.task_type
        || message?.metadata?.proactive_ai?.task_type
        || message?.metadata?.intelligence?.proactive_ai?.task_type
        || ''
    ).trim().toLowerCase();
    const generatedBy = String(answer?.generated_by || '').trim().toLowerCase();
    if (!CASE_ANALYSIS_MODES.has(mode) && taskType !== 'case_trial_analysis' && generatedBy !== 'jane_case_trial_analysis') {
        return null;
    }

    const runPlan = message?.metadata?.runPlan || message?.metadata?.intelligence?.run_plan || answer?.run_plan || null;
    const rawUrl = String(answer?.result_url || message?.metadata?.result_url || '').trim();
    const caseId = positiveInteger(answer?.case_id)
        || positiveInteger(message?.metadata?.case_id)
        || getCaseIdFromUrl(rawUrl);
    if (!caseId) return null;

    const planStatus = String(runPlan?.status || '').trim().toLowerCase();
    const failed = mode === 'case_trial_analysis_failed' || planStatus === 'failed';
    const complete = !failed && (mode === 'case_trial_analysis_complete' || planStatus === 'complete');
    const status = failed ? 'failed' : complete ? 'complete' : 'running';
    const percent = normalizePercent(runPlan?.percent ?? (complete ? 100 : null));
    const coverage = getDocumentCoverage(runPlan);
    const activeStep = (Array.isArray(runPlan?.steps) ? runPlan.steps : [])
        .find(step => ['running', 'active', 'in_progress'].includes(String(step?.status || '').trim().toLowerCase()));
    const detail = String(activeStep?.detail || '').trim();

    return {
        caseId,
        runId: positiveInteger(answer?.case_analysis_run_id || message?.metadata?.proactive_ai?.case_analysis_run_id || message?.metadata?.intelligence?.proactive_ai?.case_analysis_run_id),
        url: `/summaries/${caseId}`,
        status,
        percent,
        coverage,
        title: failed
            ? 'Case analysis needs attention'
            : complete
                ? 'Analysis ready for attorney review'
                : 'Complete case analysis is running',
        detail: detail || (failed
            ? 'Open Case Summaries to review the run details.'
            : complete
                ? 'Open the completed analysis, findings, and source coverage.'
                : 'I’m analyzing the case file in the background.'),
        actionLabel: complete ? 'Open analysis' : failed ? 'Review run' : 'View progress',
    };
};

const CaseAnalysisChatCard = ({ data, onOpen }) => {
    if (!data) return null;

    const StatusIcon = data.status === 'complete'
        ? CheckCircle2
        : data.status === 'failed'
            ? AlertTriangle
            : Loader2;
    const coverageText = data.coverage?.total > 0
        ? `${data.coverage.processed} of ${data.coverage.total} documents`
        : '';

    return (
        <div className={`library-rag-case-analysis-card ${data.status}`}>
            <div className="library-rag-case-analysis-card-header">
                <span className="library-rag-case-analysis-card-icon" aria-hidden="true"><FileSearch size={18} /></span>
                <div className="library-rag-case-analysis-card-heading">
                    <span className="library-rag-case-analysis-card-kicker">Case analysis</span>
                    <strong>{data.title}</strong>
                </div>
                <StatusIcon className={data.status === 'running' ? 'spinner' : ''} size={18} aria-hidden="true" />
            </div>
            <div className="library-rag-case-analysis-card-body">
                <div className="library-rag-case-analysis-card-meta">
                    <span>Case #{data.caseId}</span>
                    {coverageText ? <span>{coverageText}</span> : null}
                    {data.percent !== null ? <span>{data.percent}% complete</span> : null}
                </div>
                {data.status === 'running' && data.percent !== null ? (
                    <div className="library-rag-case-analysis-progress" aria-label={`${data.percent}% complete`}>
                        <span style={{ width: `${data.percent}%` }} />
                    </div>
                ) : null}
                <p>{data.detail}</p>
                <button type="button" onClick={() => onOpen?.(data.url)}>
                    <span>{data.actionLabel}</span>
                    <ExternalLink size={14} />
                </button>
            </div>
        </div>
    );
};

export default CaseAnalysisChatCard;
